import type { AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux'
import { accountAccessShowLogin } from 'actions/UiActions'
import { accountAddMobilePrompted, removeAddMobilePrompted } from 'storage/account'
import { API_CALL_SUCCESS } from 'actions/actionConstants'
import { canBroadcast, LUX_BROADCAST_CHANNEL } from 'lib/web/broadcastChannelUtils'
import { fetchAccountAgentHub as fetchAgentHubAccount } from 'actions/AgentHubActions'
import { fetchHighIntentOffers, fetchPersonalisedAlternativeOffers } from 'actions/RecommendationActions'
import {
  beginPostLoginOptimizelySync,
  trackPostLoginOptimizelySyncClientEvent,
} from 'actions/OptimizelyActions'
import { fetchPaymentCards, fetchStripePaymentCards, authLogout, fetchAvailableCredit, updateAddPhonePromptedAt, updateResolvedTrackingId } from 'actions/AuthActions'
import { matchPath } from 'react-router'
import type { Interceptor } from 'api/requestUtils'
import { registerRequestInterceptor, unregisterRequestInterceptor } from 'api/requestUtils'
import { resetContext, trackEvent } from 'analytics/analytics'
import { signinSuccessEvent, signupSuccessEvent } from 'analytics/snowplow/events'
import { ENROL_LOYALTY_ACCOUNT, FETCH_LOYALTY_ACCOUNT, USER_LOGIN, USER_REGISTRATION, USER_LOGOUT } from 'actions/apiActionConstants'
import config from 'constants/config'
import unauthorisedInterceptor from 'api/interceptors/unauthorisedInterceptor'
import moment from 'moment'
import fetchLoyaltyAccount from 'actions/loyalty/fetchLoyaltyAccount'
import fetchLoyaltyAccountPendingPoints from 'actions/loyalty/fetchLoyaltyAccountPendingPoints'
import { changeBrazeUser } from 'api/braze'
import { formatBrazeUserId } from 'api/lib/formatBrazeUserId'
import { resetTargetedPromo } from 'actions/MarketingAction'
import { getWhiteLabelAppConfig, postMessageToWhiteLabelApp } from 'lib/whitelabels/whitelabels'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { setMonitoringUser } from 'services/userMonitoring'
import { getCartMachineActor } from 'machines/cart/MultiCartMachineProvider'
import { removeItemsWithSubstringInKey } from 'lib/storage/isomorphicLocalStorage'
import { PENDING_PAYMENTS_ORDER_PREFIX } from 'storage/pendingPayments'
import {
  LEGACY_TIER_PROGRESS_SNACKBAR_STORAGE_PREFIX,
  LOYALTY_REWARDS_STORAGE_PREFIX,
} from 'loyalty/storage/loyaltyRewardsNotification'
import { setCookie } from 'lib/web/cookieUtils'
import { LAST_KNOWN_MEMBER_COOKIE } from 'analytics/snowplow/helpers/lastKnownMemberId'
import { DurationSeconds } from 'lib/datetime/durationInSeconds'
import { getCurrentIntl } from 'i18n/util/currentIntl'

function setSSRAuthToken(accessToken?: string) {
  let expires = '2018'

  if (accessToken) {
    expires = '2040'
  }

  document.cookie = `ssr_access_token=${accessToken}; expires=Wed, 11 Jul ${expires} 00:00:00 GMT; path=/; domain=${document.domain}`
}

function resetSSRAuthToken() {
  document.cookie = `ssr_access_token=''; expires=Wed, 11 Jul 2018 00:00:00 GMT; path=/; domain=${document.domain}`
}

function setLastKnownMemberIdCookie(memberId: string) {
  setCookie(LAST_KNOWN_MEMBER_COOKIE, memberId, DurationSeconds.THIRTY_DAYS, '/', 'Lax')
}

const ADD_PHONE_MODAL_VALID_ROUTES = [
  '/:regionCode',
  '/:regionCode/search',
  '/:regionCode/search/homes-and-villas',
  '/:regionCode/search/tours',
  '/:regionCode/search/cruises',
  '/:regionCode/search/car-hire',
  '/:regionCode/search/experiences',
  '/:regionCode/flights-search-results',
]
const SOCIAL_SSO_SOURCES: ReadonlyArray<App.SignUpAuthSource> = ['google', 'facebook', 'apple']
const SOCIAL_SSO_PROMPT_BRANDS: ReadonlyArray<App.BrandName> = ['luxuryescapes', 'scoopontravel', 'cudotravel', 'treatmetravel', 'dealstravel']
function isAddPhoneValidPage(currentUrl: string): boolean {
  return ADD_PHONE_MODAL_VALID_ROUTES.some(path => matchPath(currentUrl, { path, exact: true }))
}

/**
 * Returns whether we should re prompt the user to add their phone number
 */
function shouldReshowAddPhonePrompt(addPhonePromptedAt?: string): boolean {
  if (!addPhonePromptedAt) return true
  // 60 days, or ~2 months for now
  return moment(addPhonePromptedAt).isBefore(moment().subtract(60, 'days'))
}

function promptAddPhone(action: AnyAction, api: MiddlewareAPI<Dispatch<any>, App.State>) {
  const appState: App.State = api.getState()
  const basicExclusions = appState.system.headlessMode ||
    config.agentHub.isEnabled ||
    action.data.account.isSpoofed ||
    action.data.isSignUp ||
    action.data.account.phone ||
    !isAddPhoneValidPage(appState.router.location.pathname)

  const promptedBeforeLocal = accountAddMobilePrompted()

  if (promptedBeforeLocal &&
    !action.data.account.addPhonePromptedAt &&
    !action.data.account.phone
  ) {
    // Looks like we've been prompted before but never registered it with auth
    // so lets make sure we register it with auth
    api.dispatch(updateAddPhonePromptedAt(new Date()))
    removeAddMobilePrompted()
  }

  if (basicExclusions) return false
  if (!action.data.account.addPhonePromptedAt && promptedBeforeLocal) return false

  const shouldReshowPrompt = shouldReshowAddPhonePrompt(action.data.account.addPhonePromptedAt)
  return shouldReshowPrompt
}

function shouldShowAddPhoneAfterSocialSignup(action: {
  data: {
    isSignUp: boolean
    account: {
      phone?: string | null
      isSpoofed?: boolean
    }
  }
  source: App.SignUpAuthSource
}, state: App.State): boolean {
  if (!action.data.isSignUp) return false
  if (!SOCIAL_SSO_SOURCES.includes(action.source)) return false
  if (!SOCIAL_SSO_PROMPT_BRANDS.includes(config.BRAND)) return false
  if (state.system.headlessMode) return false
  if (config.agentHub.isEnabled) return false
  if (action.data.account.isSpoofed) return false
  if (action.data.account.phone) return false
  return isAddPhoneValidPage(state.router.location.pathname)
}

function isLoginOrRegisterSuccess(action: unknown): action is {
  type: string
  api: string
  data: any
  supressBroadcast: boolean
  source: App.SignUpAuthSource
} {
  return !!action &&
    typeof action === 'object' &&
    'api' in action &&
    'type' in action &&
    (action.api === USER_LOGIN || action.api === USER_REGISTRATION) &&
    action.type === API_CALL_SUCCESS
}

let interceptor: Interceptor
const userMiddleware: Middleware<{}, App.State, Dispatch<any>> = (api) => next => (action) => {
  if (
    !!action &&
    typeof action === 'object' &&
    'api' in action &&
    'type' in action
  ) {
    if (isLoginOrRegisterSuccess(action)) {
      api.dispatch(beginPostLoginOptimizelySync())
      trackPostLoginOptimizelySyncClientEvent('started')
      // by running next, the rest of the reducer flow goes through
      // and we now have the state "after" the dispatch is done
      next(action)

      const state = api.getState()
      // The fetching below relies on the account being populated in the reducer
      api.dispatch(fetchAvailableCredit())
      api.dispatch(fetchPaymentCards())
      api.dispatch(fetchStripePaymentCards())
      api.dispatch(fetchHighIntentOffers())
      api.dispatch(fetchPersonalisedAlternativeOffers())
      api.dispatch(updateResolvedTrackingId())

      const isLoyaltyEnabled = getIsLoyaltyEnabled(state)

      if (config.agentHub.isEnabled) {
        api.dispatch(fetchAgentHubAccount())
      }

      setSSRAuthToken(action.data.accessToken)

      // Set last known member ID cookie for analytics stitching
      if (action.data.account.memberId) {
        setLastKnownMemberIdCookie(action.data.account.memberId)
      }

      // always clean up any previous interceptors
      unregisterRequestInterceptor(interceptor)
      // log them out if an api returns a 401, probably means their connection is no longer authorised!
      // when we unregister need it to be the same function, so save the one we registered with here
      interceptor = unauthorisedInterceptor(() => api.dispatch(authLogout()))
      registerRequestInterceptor(interceptor)

      const isSignUp = action.data.isSignUp && !action.supressBroadcast

      changeBrazeUser(action.data.account, { isSignUp })

      if (getWhiteLabelAppConfig().isApp && action.data.account.memberId) {
        const user = {
          memberId: formatBrazeUserId(action.data.account.memberId),
          email: action.data.account.email,
          firstName: action.data.account.givenName,
          lastName: action.data.account.surname,
          countryCode: action.data.account.countryCode,
          phone: action.data.account.phone,
          phonePrefix: action.data.account.phonePrefix,
        }
        postMessageToWhiteLabelApp({ user })
      }

      if (canBroadcast() && !action.supressBroadcast) {
        const channel = new BroadcastChannel(LUX_BROADCAST_CHANNEL)
        // we just logged in, let other tabs/windows know about it
        channel.postMessage({ action })
        channel.close()
      }
      // If it's a signup and not an action from another tab/window
      if (isSignUp) {
        trackEvent(signupSuccessEvent(action.source))

        if (!isLoyaltyEnabled) {
          const intl = getCurrentIntl()
          showSnackbar('', 'success', {
            heading: intl.formatMessage({
              id: '_o2xfr6XlMzhumCTckgB',
              defaultMessage: 'Welcome to {title}',
              description: 'Welcome snackbar heading shown after successful user signup',
            }, { title: config.title }),
          })
        }

        api.dispatch(enrolLoyaltyAccount({
          enrolment: 'newUserRegistration',
        }))

        if (shouldShowAddPhoneAfterSocialSignup(action, state)) {
          api.dispatch(accountAccessShowLogin('promptAddPhoneOnboarding'))
        }
      } else {
        api.dispatch(fetchLoyaltyAccount())
      }

      // If it's a signin and not an action from another tab/window
      if (action.data.isSignIn && !action.supressBroadcast) {
        trackEvent(signinSuccessEvent(action.source))
      }

      // Send successful login event to cart machine
      getCartMachineActor()?.send({ type: 'successful login', memberId: action.data.account.memberId })

      // prompt for adding mobile if they don't have one
      // added here so it also picks up social logins
      if (promptAddPhone(action, api)) {
        api.dispatch(accountAccessShowLogin('promptAddPhone'))
      }

      return
    } else if (
      (action.api === FETCH_LOYALTY_ACCOUNT || action.api === ENROL_LOYALTY_ACCOUNT) &&
      action.type === API_CALL_SUCCESS
    ) {
      next(action)
      api.dispatch(fetchLoyaltyAccountPendingPoints())
      return
    } else if (action.api === USER_LOGOUT && action.type === API_CALL_SUCCESS) {
      unregisterRequestInterceptor(interceptor)
      resetSSRAuthToken()

      removeItemsWithSubstringInKey(PENDING_PAYMENTS_ORDER_PREFIX)
      removeItemsWithSubstringInKey(LOYALTY_REWARDS_STORAGE_PREFIX)
      removeItemsWithSubstringInKey(LEGACY_TIER_PROGRESS_SNACKBAR_STORAGE_PREFIX)

      api.dispatch(resetTargetedPromo())

      getCartMachineActor()?.send({ type: 'logout user' })

      if (canBroadcast() && 'supressBroadcast' in action && !action.supressBroadcast) {
        const channel = new BroadcastChannel(LUX_BROADCAST_CHANNEL)
        // we just logged out, let other tabs/windows know about it
        channel.postMessage({ action })
        channel.close()
      }
      resetContext()
      setMonitoringUser(null)
    }
  }

  return next(action)
}

export default userMiddleware
